import React, { useLayoutEffect } from 'react';
import { errorRing, styled, useUniqueId, } from '@workday/canvas-kit-react/common';
import { colors, borderRadius, inputColors, spaceNumbers, type, space, } from '@workday/canvas-kit-react/tokens';
import { caretDownSmallIcon } from '@workday/canvas-system-icons-web';
import { SystemIcon } from '@workday/canvas-kit-react/icon';
import { SelectMenu } from './SelectMenu';
import { SelectOption } from './SelectOption';
import { scrollIntoViewIfNeeded } from './scrolling';
import { getCorrectedIndexByValue } from './utils';
export const buttonBorderWidth = 1;
export const buttonDefaultWidth = 280;
const menuIconSize = 24;
const buttonPadding = spaceNumbers.xxs - buttonBorderWidth;
const SelectButton = styled('button')({
    ...type.levels.subtext.large,
    border: `${buttonBorderWidth}px solid ${inputColors.border}`,
    cursor: 'default',
    display: 'block',
    backgroundColor: inputColors.background,
    borderRadius: borderRadius.m,
    boxSizing: 'border-box',
    height: space.xl,
    outline: 'none',
    overflow: 'hidden',
    padding: buttonPadding,
    paddingRight: spaceNumbers.xxs + menuIconSize + buttonPadding,
    textAlign: 'left',
    textOverflow: 'ellipsis',
    transition: '0.2s box-shadow, 0.2s border-color',
    whiteSpace: 'nowrap',
    // width is required (instead of minWidth) in order for the button to
    // be sized properly for lengthy options
    width: buttonDefaultWidth,
    '&::placeholder': {
        color: inputColors.placeholder,
    },
    '&:disabled': {
        backgroundColor: inputColors.disabled.background,
        borderColor: inputColors.disabled.border,
        color: inputColors.disabled.text,
        '&::placeholder': {
            color: inputColors.disabled.text,
        },
    },
}, ({ error, menuVisibility, theme }) => {
    const themedFocusOutlineColor = theme.canvas.palette.common.focusOutline;
    const buttonFocusStyles = {
        borderColor: themedFocusOutlineColor,
        boxShadow: `inset 0 0 0 1px ${themedFocusOutlineColor}`,
    };
    if (error === undefined) {
        // If there isn't an error, apply focus and hover styles if the menu is
        // closed or in the process of closing (otherwise, the menu is opened
        // or in the process of opening: style the button as if it had focus)
        return menuVisibility === 'closed' || menuVisibility === 'closing'
            ? {
                '&:focus:not([disabled])': {
                    ...buttonFocusStyles,
                },
                '&:hover:not([disabled]):not(:focus)': {
                    borderColor: inputColors.hoverBorder,
                },
            }
            : { ...buttonFocusStyles };
    }
    return {
        ...errorRing(error, theme),
    };
}, ({ grow }) => grow && {
    width: '100%',
});
const SelectMenuIcon = styled(SystemIcon)({
    position: 'absolute',
    top: space.xxxs,
    right: space.xxxs,
    padding: space.xxxs,
    pointerEvents: 'none',
    '& path': {
        transition: '100ms fill',
    },
});
const SelectInput = styled('input')({
    display: 'none',
});
const SelectWrapper = styled('div')({
    position: 'relative',
}, ({ grow }) => ({
    display: grow ? 'block' : 'inline-block',
}), ({ disabled }) => ({
    '&:hover .menu-icon path': {
        fill: disabled ? undefined : colors.licorice500,
    },
}));
const defaultRenderOption = option => {
    return React.createElement("div", null, defaultRenderSelected(option));
};
const defaultRenderSelected = option => {
    return option.label;
};
export const SelectBase = ({ 'aria-labelledby': ariaLabelledBy, 'aria-required': ariaRequired, as, forwardedButtonRef, localButtonRef, disabled, error, focusedOptionIndex = 0, grow, inputRef, menuPlacement = 'bottom', menuRef, menuVisibility = 'closed', onChange, onKeyDown, onClose, onOptionSelection, options, renderOption = defaultRenderOption, renderSelected = defaultRenderSelected, required, shouldMenuAutoFlip = true, shouldMenuAutoFocus = true, value, ...elemProps }) => {
    const focusedOptionRef = React.useRef(null);
    const menuId = useUniqueId();
    const renderOptions = (renderOption) => {
        const selectedOptionIndex = getCorrectedIndexByValue(options, value);
        return options.map((option, index) => {
            const optionProps = {
                'aria-disabled': option.disabled ? true : undefined,
                'aria-selected': selectedOptionIndex === index ? true : undefined,
                error,
                focused: focusedOptionIndex === index,
                id: option.id,
                interactive: menuVisibility === 'opening' || menuVisibility === 'opened',
                key: option.id,
                optionRef: focusedOptionIndex === index ? focusedOptionRef : undefined,
                value: option.value,
                ...(onOptionSelection
                    ? {
                        onClick: (event) => {
                            // TODO: Figure out why this preventDefault call exists.
                            // Removing it doesn't have any obvious consequences,
                            // but need to do more careful testing.
                            event.preventDefault();
                            onOptionSelection(index);
                        },
                    }
                    : {}),
            };
            // Pass in additional information about the option state: focused, selected
            const normalizedOption = {
                ...option,
                focused: optionProps.focused,
                selected: !!optionProps['aria-selected'],
            };
            return React.createElement(SelectOption, Object.assign({}, optionProps), renderOption(normalizedOption));
        });
    };
    // If the focused option changed, scroll the newly focused option into view (if
    // necessary) but do NOT center it
    useLayoutEffect(() => {
        const focusedOption = focusedOptionRef.current;
        if (focusedOption) {
            // TODO: Figure out if rAF is the best approach here. I initially added
            // rAF to get the Select States Menu On story to render correctly in IE.
            // Without rAF, the menu is scrolled slightly further down than it should
            // be (only in IE, and only in the Menu On visual testing stories), which
            // triggers a visual regression. We're inside useLayoutEffect here so I
            // didn't expect to need rAF in order to make proper measurements, but it
            // seems to be necessary in IE.
            //
            // This rAF call also has the additional benefit of fixing a jarring menu
            // placement issue in IE (https://github.com/Workday/canvas-kit/issues/791),
            // so I'm leaving it in for now.
            const animateId = requestAnimationFrame(() => {
                // We cannot use the native Element.scrollIntoView() here because it
                // doesn't work properly with the portalled menu: when using the keyboard
                // to advance focus through the options, using scrollIntoView to keep the
                // newly focused option in view also scrolls the ENTIRE page. Instead, we
                // call our own scrollIntoViewIfNeeded function.
                scrollIntoViewIfNeeded(focusedOption, false);
            });
            return () => {
                cancelAnimationFrame(animateId);
            };
        }
        return undefined;
    }, [focusedOptionIndex]);
    // If the menu was just opened, scroll the focused option into view (if
    // necessary) and center it
    useLayoutEffect(() => {
        const focusedOption = focusedOptionRef.current;
        // We need to scroll if the menu is either opening or opened in case we decide to
        // bypass the opening state and jump straight to opened (like in visual testing,
        // for instance)
        if (focusedOption && (menuVisibility === 'opening' || menuVisibility === 'opened')) {
            // TODO: This rAF call is also necessary for the Menu On visual testing
            // stories to render properly in IE (see above, both rAF calls need to be
            // present). It has no bearing on the menu placement issue.
            const animateId = requestAnimationFrame(() => {
                scrollIntoViewIfNeeded(focusedOption, true);
            });
            return () => {
                cancelAnimationFrame(animateId);
            };
        }
        return undefined;
    }, [menuVisibility]);
    // Do a bit of error-checking in case options weren't provided
    const hasOptions = options.length > 0;
    const selectedOption = hasOptions ? options[getCorrectedIndexByValue(options, value)] : null;
    const selectedOptionValue = selectedOption ? selectedOption.value : '';
    return (React.createElement(SelectWrapper, { grow: grow, disabled: disabled },
        React.createElement(SelectButton, Object.assign({ "aria-expanded": menuVisibility !== 'closed' ? 'true' : undefined, "aria-haspopup": "listbox", "aria-controls": menuVisibility !== 'closed' ? menuId : undefined, as: as, disabled: disabled, error: error, grow: grow, menuVisibility: menuVisibility, onKeyDown: onKeyDown, 
            // Prevent Firefox from triggering click handler on spacebar during
            // type-ahead when the menu is closed (and, thus, incorrectly displaying
            // the menu)
            onKeyUp: e => {
                e.preventDefault();
            }, ref: forwardedButtonRef, type: "button", value: selectedOptionValue }, elemProps), !!selectedOption && renderSelected(selectedOption)),
        React.createElement(SelectInput, { onChange: onChange, ref: inputRef, type: "text", value: selectedOptionValue }),
        hasOptions && menuVisibility !== 'closed' && (React.createElement(SelectMenu, { "aria-activedescendant": options[focusedOptionIndex].id, "aria-labelledby": ariaLabelledBy, "aria-required": ariaRequired || required ? true : undefined, buttonRef: localButtonRef, id: menuId, error: error, menuRef: menuRef, onKeyDown: onKeyDown, onClose: onClose, placement: menuPlacement, shouldAutoFlip: shouldMenuAutoFlip, shouldAutoFocus: shouldMenuAutoFocus, visibility: menuVisibility }, renderOptions(renderOption))),
        React.createElement(SelectMenuIcon, { className: "menu-icon", icon: caretDownSmallIcon, color: disabled ? colors.licorice100 : colors.licorice200, colorHover: disabled ? colors.licorice100 : colors.licorice500, size: menuIconSize })));
};
